import { createContext, memo, type ReactNode, type RefObject, useContext, useMemo } from 'react';

export type MouseSelectionContextValue = {
  /**
   * The SELECTION AREA: the surface where useSelectedItemsMouseSelection's deselect-on-mousedown listener
   * binds — a plain click inside it clears the current selection.
   */
  selectionArea: RefObject<HTMLElement | null>;
};

const MouseSelectionContext = createContext<MouseSelectionContextValue | null>(null);

export type MouseSelectionProviderProps = {
  children: ReactNode;
  selectionArea: MouseSelectionContextValue['selectionArea'];
};

/**
 * Scopes mouse selection to a bounded selection area. OPTIONAL by design: a surface that mounts
 * no provider gets `window` — a plain click anywhere clears the selection — so mounting one is a
 * page's deliberate declaration that deselection is bounded to this element.
 *
 * Providers resolve nearest-first, so a surface that renders its own selection area inside another
 * (e.g. the collection link editor's edit canvas rendering a public page inside the app shell)
 * expresses "the selection area this list lives in" structurally instead of relying on a
 * document-order `#main` lookup.
 */
export const MouseSelectionProvider = memo(({ children, selectionArea }: MouseSelectionProviderProps) => {
  const value = useMemo(() => ({ selectionArea }), [selectionArea]);

  return <MouseSelectionContext.Provider value={value}>{children}</MouseSelectionContext.Provider>;
});

MouseSelectionProvider.displayName = 'MouseSelectionProvider';

/**
 * Null outside any provider — a designed state, not an error: useSelectedItemsMouseSelection treats it as
 * "no bounded area declared" and binds `window`.
 */
export const useMouseSelectionContext = () => useContext(MouseSelectionContext);
